/**
 * Admin → Tenant Health (T1.2, 2026-05-28)
 *
 * Tenant-facing aggregate health snapshot. Closes the Discovery
 * Independence Principle gap (CLAUDE.md §14): standalone customers
 * need their own self-service health page, not a Core link or a
 * super-admin (FOCTTA-staff) surface.
 *
 * Backend: GET /v1/admin/health (modules/admin/health/routes.ts).
 * Returns a TenantHealthSnapshot with 6 subsystems + overall verdict.
 *
 * Layout: overall verdict banner at the top, then the subsystem cards
 * (audit chain / scanners / edge fleet / audit vault / error rate /
 * compound correlator), three to a row on a wide screen so the six are
 * read without scrolling. Each card degrades gracefully if its subsystem
 * reported an error.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Database,
  HelpCircle,
  Loader2,
  Radar,
  RefreshCw,
  Server,
  Shield,
  TrendingUp,
} from 'lucide-react';

import { GlassCard, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, HelpTip, LoadingState } from '@foctta/ui-shell';
import {
  ApiError,
  getTenantHealth,
  mirrorAuditVault,
  verifyAuditVault,
  type TenantHealthSnapshot,
  type TenantHealthChainVerification,
  type SubsystemOrError,
} from '@/lib/api';

// ── Helpers ─────────────────────────────────────────────────────

function hasError<T>(s: SubsystemOrError<T>): s is { error: string } {
  return s !== null && typeof s === 'object' && 'error' in s;
}

function timeAgo(iso: string | null, now: number = Date.now()): string {
  if (!iso) return '-';
  const ms = now - new Date(iso).getTime();
  if (ms < 0) return 'just now';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/**
 * The audit-chain integrity hint. `null` means no verification has finished,
 * so the page says so instead of "chain intact" (R3-OI-192: the server used to
 * send a hard-coded `true`). Since release 3.3 (C3) a scheduled job verifies
 * the whole chain and the server sends its real result.
 */
export function chainIntegrityHint(chainIntact: boolean | null): string {
  if (chainIntact === null) return 'not verified yet';
  return chainIntact ? 'chain intact' : 'TAMPERED';
}

/**
 * The "Last verified" tile: when the newest scheduled verification ran and
 * what it found. Never a constant: no run yet reads "not verified yet".
 */
export function lastVerifiedTile(
  last: TenantHealthChainVerification | null | undefined,
  now: number = Date.now(),
  conclusive?: TenantHealthChainVerification | null,
): { value: string; hint: string; broken: boolean } {
  if (!last) return { value: 'Not verified yet', hint: 'the first scheduled check has not finished', broken: false };
  // The newest check could not read the chain: a break an earlier check found
  // still stands and is shown, never hidden behind the failed read.
  if (last.status === 'error' && conclusive?.status === 'broken') {
    return {
      value: timeAgo(conclusive.verifiedAt, now),
      hint: `BROKEN at block ${conclusive.brokenAtBlockIndex ?? '?'}${conclusive.detail ? `: ${conclusive.detail}` : ''} (the latest check could not read the chain)`,
      broken: true,
    };
  }
  const when = timeAgo(last.verifiedAt, now);
  if (last.status === 'intact') {
    return { value: when, hint: `chain intact - ${fmtNum(last.blocksScanned)} blocks checked`, broken: false };
  }
  if (last.status === 'broken') {
    return {
      value: when,
      hint: `BROKEN at block ${last.brokenAtBlockIndex ?? '?'}${last.detail ? `: ${last.detail}` : ''}`,
      broken: true,
    };
  }
  return { value: when, hint: 'could not be verified - the chain could not be read', broken: false };
}

function fmtNum(n: number): string {
  return new Intl.NumberFormat('en-IN').format(n);
}

// ── Subsystem cards ─────────────────────────────────────────────

function SubsystemCard({
  title,
  icon: Icon,
  description,
  errorMsg,
  children,
}: {
  title: string;
  icon: typeof Activity;
  description?: string;
  errorMsg?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <GlassCard className="p-0">
      {/* The "?" sits beside the heading, not inside it: inside, the heading
          was read out as "Audit chain About Audit chain". */}
      <CardHeader className="flex-row items-center gap-2 space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="h-4 w-4 text-primary" />
          {title}
        </CardTitle>
        {description ? <HelpTip label={`About ${title}`}>{description}</HelpTip> : null}
      </CardHeader>
      <div className="px-4 pb-4">
        {errorMsg ? (
          <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-md">
            <AlertCircle className="h-4 w-4" />
            <span>Subsystem unavailable: {errorMsg}</span>
          </div>
        ) : (
          children
        )}
      </div>
    </GlassCard>
  );
}

function StatBlock({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-xl font-semibold">{value}</div>
      {hint ? <div className="text-xs text-muted-foreground mt-0.5">{hint}</div> : null}
    </div>
  );
}

// On-demand audit-vault actions (T1.3). DPO/IT-Admin can force a WORM
// mirror before an audit, or prove chain integrity on demand. Both calls
// require discovery:write; a 403 surfaces as the result message.
function VaultActions({ onMirrored }: { onMirrored: () => void }): JSX.Element {
  const [busy, setBusy] = useState<'mirror' | 'verify' | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [tone, setTone] = useState<'ok' | 'warn' | 'err'>('ok');

  const runMirror = useCallback(async (): Promise<void> => {
    setBusy('mirror');
    setMsg(null);
    try {
      const r = await mirrorAuditVault();
      setTone(r.blocksFailed > 0 ? 'warn' : 'ok');
      setMsg(
        `Mirror: ${fmtNum(r.blocksMirrored)} mirrored, ${fmtNum(r.blocksFailed)} failed ` +
          `(${fmtNum(r.blocksScanned)} scanned, ${r.durationMs}ms).`,
      );
      onMirrored();
    } catch (e) {
      setTone('err');
      setMsg(e instanceof Error ? e.message : 'mirror failed');
    } finally {
      setBusy(null);
    }
  }, [onMirrored]);

  const runVerify = useCallback(async (): Promise<void> => {
    setBusy('verify');
    setMsg(null);
    try {
      const r = await verifyAuditVault();
      const bad = r.blocksTampered + r.blocksMissing;
      setTone(bad > 0 ? 'err' : 'ok');
      setMsg(
        bad > 0
          ? `Verify: ${fmtNum(r.blocksTampered)} TAMPERED, ${fmtNum(r.blocksMissing)} missing ` +
            `of ${fmtNum(r.blocksChecked)} checked.`
          : `Verify: all ${fmtNum(r.blocksVerified)} blocks intact (${fmtNum(r.blocksChecked)} checked).`,
      );
    } catch (e) {
      setTone('err');
      setMsg(e instanceof Error ? e.message : 'verify failed');
    } finally {
      setBusy(null);
    }
  }, []);

  return (
    <div className="space-y-2 pt-1">
      <div className="flex gap-2">
        <Button
          data-action="disc.adm_health.a01"
          variant="outline"
          size="sm"
          onClick={() => void runMirror()}
          disabled={busy !== null}
        >
          {busy === 'mirror' ? 'Mirroring…' : 'Mirror now'}
        </Button>
        <Button
          data-action="disc.adm_health.a02"
          variant="outline"
          size="sm"
          onClick={() => void runVerify()}
          disabled={busy !== null}
        >
          {busy === 'verify' ? 'Verifying…' : 'Verify integrity'}
        </Button>
      </div>
      {msg ? (
        <div
          className={
            'text-xs ' +
            (tone === 'err'
              ? 'text-destructive'
              : tone === 'warn'
                ? 'text-amber-600'
                : 'text-muted-foreground')
          }
        >
          {msg}
        </div>
      ) : null}
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────

export function AdminHealth(): JSX.Element {
  const [snap, setSnap] = useState<TenantHealthSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // GET /v1/admin/health needs `admin:health:read` (IT Admin, DPO, platform
  // staff) since 2026-09-27 (review of PR #84, R84-5). Any other role gets a
  // 403: say who the page is for, and stop polling a door that will not open.
  const [forbidden, setForbidden] = useState(false);
  const forbiddenRef = useRef(false);

  const refresh = useCallback(async (): Promise<void> => {
    if (forbiddenRef.current) return;
    setLoading(true);
    setError(null);
    try {
      const r = await getTenantHealth();
      setSnap(r);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        forbiddenRef.current = true;
        setForbidden(true);
      } else {
        setError(e instanceof Error ? e.message : 'failed to load tenant health');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    // Auto-refresh every 30s so the page reflects live state without
    // requiring a manual reload. Tab-blurred = no fetch (visibilitychange).
    const handle = window.setInterval(() => {
      if (!document.hidden) void refresh();
    }, 30_000);
    return () => window.clearInterval(handle);
  }, [refresh]);

  const overall = snap?.overall;
  const OverallIcon = useMemo(() => {
    if (!overall) return HelpCircle;
    if (overall.status === 'healthy') return CheckCircle2;
    if (overall.status === 'degraded') return AlertTriangle;
    return HelpCircle;
  }, [overall]);

  if (forbidden) {
    return (
      <PageContainer>
        <PageHeader title="System Health" subtitle="Operational snapshot" />
        <ErrorState
          title="System Health is for IT Admins and DPOs"
          detail="Your role cannot view the operational health snapshot. Ask your IT Admin or DPO if you need it."
          severity="warning"
        />
      </PageContainer>
    );
  }

  if (loading && !snap) {
    return (
      <PageContainer>
        <PageHeader title="System Health" subtitle="Operational snapshot" />
        <LoadingState caption="Computing health snapshot…" />
      </PageContainer>
    );
  }

  if (error && !snap) {
    return (
      <PageContainer>
        <PageHeader title="System Health" subtitle="Operational snapshot" />
        <ErrorState
          title="Couldn't compute system health"
          detail={error}
          onRetry={refresh}
          severity="critical"
        />
      </PageContainer>
    );
  }

  if (!snap) return <PageContainer>{null}</PageContainer>;

  return (
    <PageContainer>
      <PageHeader
        title="System Health"
        subtitle="Aggregate operational snapshot - audit chain, scanners, edge fleet, audit-vault mirror status, recent error rate, compound correlator. Refreshes every 30 seconds."
        actions={
          <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        }
      />

      {/* Overall verdict banner */}
      <GlassCard
        className={`mb-4 ${
          overall?.status === 'healthy'
            ? 'border-success/40'
            : overall?.status === 'degraded'
            ? 'border-warning/40'
            : ''
        }`}
      >
        <div className="flex items-center gap-4">
          <OverallIcon
            className={`h-8 w-8 ${
              overall?.status === 'healthy'
                ? 'text-success'
                : overall?.status === 'degraded'
                ? 'text-warning'
                : 'text-muted-foreground'
            }`}
          />
          <div className="flex-1">
            <div className="text-lg font-semibold capitalize">
              Overall: {overall?.status ?? 'unknown'}
            </div>
            <div className="text-xs text-muted-foreground">
              Snapshot generated {timeAgo(snap.generatedAt)} (
              {new Date(snap.generatedAt).toLocaleString()})
            </div>
            {overall?.weakSpots.length ? (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {overall.weakSpots.map((w) => (
                  <Badge key={w} variant="warning" className="text-xs">
                    {w}
                  </Badge>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </GlassCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="health-subsystems">
        {/* Audit chain */}
        <SubsystemCard
          title="Audit chain"
          icon={Shield}
          description="Hash-chained per-tenant audit log (DPDPA + SEBI evidence)."
          errorMsg={hasError(snap.auditChain) ? snap.auditChain.error : undefined}
        >
          {!hasError(snap.auditChain) && (
            <div className="grid grid-cols-2 gap-4">
              <StatBlock
                label="Total blocks"
                value={fmtNum(snap.auditChain.totalBlocks)}
              />
              <StatBlock
                label="Head index"
                value={
                  snap.auditChain.headBlockIndex !== null
                    ? fmtNum(snap.auditChain.headBlockIndex)
                    : '-'
                }
              />
              <StatBlock
                label="Last write"
                value={timeAgo(snap.auditChain.lastWriteAt)}
                hint={chainIntegrityHint(snap.auditChain.chainIntact)}
              />
              {(() => {
                const tile = lastVerifiedTile(
                  snap.auditChain.lastVerification,
                  Date.now(),
                  snap.auditChain.lastConclusiveVerification,
                );
                return (
                  <div data-testid="audit-chain-last-verified">
                    <div className="text-xs text-muted-foreground">Last verified</div>
                    <div className="text-xl font-semibold">{tile.value}</div>
                    <div
                      className={`text-xs mt-0.5 ${tile.broken ? 'text-red-700 dark:text-red-400 font-medium' : 'text-muted-foreground'}`}
                    >
                      {tile.hint}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </SubsystemCard>

        {/* Scanners */}
        <SubsystemCard
          title="Activity scanners (24h)"
          icon={Radar}
          description="UEBA / Sonar / Early Warning finding production over the last 24 hours."
          errorMsg={hasError(snap.scanners) ? snap.scanners.error : undefined}
        >
          {!hasError(snap.scanners) &&
            (() => {
              // Bind to a const inside the IIFE so TypeScript can carry
              // the narrowed type through the .map() closure (control-flow
              // narrowing doesn't reach into arrow-fn bodies otherwise).
              const scanners = snap.scanners;
              return (
                <div className="space-y-3">
                  {(['ueba', 'sonar', 'earlyWarning'] as const).map((k) => {
                    const s = scanners[k];
                    const label =
                      k === 'ueba' ? 'UEBA' : k === 'sonar' ? 'Sonar' : 'Early Warning';
                    return (
                      <div
                        key={k}
                        className="flex items-center justify-between text-sm"
                      >
                        <div className="font-medium">{label}</div>
                        <div className="text-xs text-muted-foreground">
                          {fmtNum(s.recent24h)} findings · last {timeAgo(s.lastFindingAt)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
        </SubsystemCard>

        {/* Edge fleet */}
        <SubsystemCard
          title="Agents"
          icon={Server}
          description="Heartbeat-derived pod-state rollup."
          errorMsg={hasError(snap.edgeFleet) ? snap.edgeFleet.error : undefined}
        >
          {!hasError(snap.edgeFleet) && (
            <>
              <StatBlock label="Total" value={fmtNum(snap.edgeFleet.total)} />
              <div className="grid grid-cols-3 gap-3 mt-3 text-xs">
                <div>
                  <span className="text-muted-foreground">Healthy</span>
                  <div className="text-success font-semibold">
                    {fmtNum(snap.edgeFleet.healthy)}
                  </div>
                </div>
                <div>
                  <span className="text-muted-foreground">Unhealthy</span>
                  <div className="text-warning font-semibold">
                    {fmtNum(snap.edgeFleet.degraded)}
                  </div>
                </div>
                <div>
                  <span className="text-muted-foreground">Offline</span>
                  <div className="text-warning font-semibold">
                    {fmtNum(snap.edgeFleet.stale)}
                  </div>
                </div>
                <div>
                  <span className="text-muted-foreground">Down</span>
                  <div className="text-destructive font-semibold">
                    {fmtNum(snap.edgeFleet.down)}
                  </div>
                </div>
                <div>
                  <span className="text-muted-foreground">Pending</span>
                  <div className="font-semibold">{fmtNum(snap.edgeFleet.pending)}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Decom.</span>
                  <div className="font-semibold">
                    {fmtNum(snap.edgeFleet.decommissioned)}
                  </div>
                </div>
              </div>
            </>
          )}
        </SubsystemCard>

        {/* Audit-vault */}
        <SubsystemCard
          title="Audit-vault (S3 WORM)"
          icon={Database}
          description="Cross-product immutable evidence mirror status."
          errorMsg={hasError(snap.auditVault) ? snap.auditVault.error : undefined}
        >
          {!hasError(snap.auditVault) && (
            <div className="space-y-3">
              {snap.auditVault.bucket === null ? (
                <div className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-md">
                  Audit-vault disabled (no <code>AUDIT_VAULT_S3_BUCKET</code> configured).
                  Local Postgres-backed audit chain remains the source of truth.
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  <StatBlock label="Total" value={fmtNum(snap.auditVault.totalBlocks)} />
                  <StatBlock
                    label="Mirrored"
                    value={fmtNum(snap.auditVault.mirroredBlocks)}
                  />
                  <StatBlock
                    label="Pending"
                    value={fmtNum(snap.auditVault.unmirroredBlocks)}
                    hint={
                      snap.auditVault.oldestUnmirroredAt
                        ? `oldest ${timeAgo(snap.auditVault.oldestUnmirroredAt)}`
                        : undefined
                    }
                  />
                </div>
              )}
              <div className="text-xs text-muted-foreground">
                Retention: {snap.auditVault.retentionYears} years (
                {snap.auditVault.bucket ?? 'not configured'})
              </div>
              {snap.auditVault.bucket !== null ? (
                <VaultActions onMirrored={() => void refresh()} />
              ) : null}
            </div>
          )}
        </SubsystemCard>

        {/* Error rate */}
        <SubsystemCard
          title="Request errors (last 1h)"
          icon={AlertTriangle}
          description="Captured by the request-trace recorder."
          errorMsg={hasError(snap.errorRate) ? snap.errorRate.error : undefined}
        >
          {!hasError(snap.errorRate) && (
            <div className="grid grid-cols-3 gap-4">
              <StatBlock label="Total errors" value={fmtNum(snap.errorRate.total)} />
              <StatBlock
                label="4xx (client)"
                value={fmtNum(snap.errorRate.status4xx)}
              />
              <StatBlock
                label="5xx (server)"
                value={fmtNum(snap.errorRate.status5xx)}
                hint={snap.errorRate.status5xx > 0 ? 'needs review' : undefined}
              />
            </div>
          )}
        </SubsystemCard>

        {/* Compound correlator */}
        <SubsystemCard
          title="Compound correlator"
          icon={TrendingUp}
          description="Cross-engine kill-chain detection."
          errorMsg={hasError(snap.compound) ? snap.compound.error : undefined}
        >
          {!hasError(snap.compound) && (
            <div className="grid grid-cols-2 gap-4">
              <StatBlock
                label="Open compound findings"
                value={fmtNum(snap.compound.openCompoundFindings)}
              />
              <StatBlock
                label="Last signal"
                value={timeAgo(snap.compound.lastSignalAt)}
              />
            </div>
          )}
        </SubsystemCard>
      </div>
    </PageContainer>
  );
}

export default AdminHealth;
